import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeApi, projectApi, companyApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Plus, Users, Filter, Download, User } from 'lucide-react';
import { toast } from 'sonner';
import { DEPARTMENTS, DESIGNATIONS } from '@/lib/employeeOptions';
import { downloadBlobFile } from '@/lib/exportFile';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';

const employeeDisplayName = (employee) =>
  `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim();

export default function EmployeeListPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companiesCatalog, setCompaniesCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [designationFilter, setDesignationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [employeeDirectory, setEmployeeDirectory] = useState([]);

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
  };

  const normalizeEmployeeListResponse = (value) => {
    if (Array.isArray(value)) {
      return { employees: value, total: value.length, page: 1, pages: 1 };
    }
    if (Array.isArray(value?.items)) {
      return {
        employees: value.items,
        total: value?.total ?? value.items.length,
        page: value?.page ?? 1,
        pages: value?.pages ?? 1,
      };
    }
    if (Array.isArray(value?.employees)) {
      return {
        employees: value.employees,
        total: value?.total ?? value.employees.length,
        page: value?.page ?? 1,
        pages: value?.pages ?? 1,
      };
    }
    return { employees: [], total: 0, page: 1, pages: 1 };
  };

  const queryFilters = useMemo(() => {
    const params = { page, limit: pageSize };
    const trimmedSearch = debouncedSearch?.trim?.();
    if (trimmedSearch) params.search = trimmedSearch;
    if (deptFilter !== 'all') params.department = deptFilter;
    if (designationFilter !== 'all') params.designation = designationFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (companyFilter !== 'all') params.company = companyFilter;
    if (projectFilter !== 'all') params.project = projectFilter;
    return params;
  }, [page, pageSize, debouncedSearch, deptFilter, designationFilter, statusFilter, companyFilter, projectFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empsResponse, projs, companiesResponse] = await Promise.all([
          employeeApi.getAll(queryFilters).catch(() => []),
          projectApi.getAll().catch(() => []),
          companyApi.getAll().catch(() => []),
        ]);
        const normalizedEmployeeResponse = normalizeEmployeeListResponse(empsResponse);
        const normalizedProjects = normalizeArrayResponse(projs);
        const normalizedCompanies = normalizeArrayResponse(companiesResponse);
        setEmployees(normalizedEmployeeResponse.employees);
        setTotalEmployees(normalizedEmployeeResponse.total);
        setTotalPages(normalizedEmployeeResponse.pages);
        setPage((p) => clampPageToTotal(p, normalizedEmployeeResponse.pages));
        setProjects(normalizedProjects);
        setCompaniesCatalog(normalizedCompanies);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [queryFilters]);

  useEffect(() => {
    employeeApi.getAll({ sortBy: 'name' }).then((data) => {
      setEmployeeDirectory(normalizeArrayResponse(data));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, deptFilter, designationFilter, statusFilter, companyFilter, projectFilter]);

  const managerNameById = useMemo(() => {
    const map = new Map();
    [...employeeDirectory, ...employees].forEach((person) => {
      const name = employeeDisplayName(person);
      if (!name) return;
      if (person?.empId) map.set(String(person.empId), name);
      if (person?.id) map.set(String(person.id), name);
    });
    return map;
  }, [employeeDirectory, employees]);

  const getReportingManagerName = (emp) => {
    const raw = emp?.reportingManager;
    if (raw == null || raw === '' || raw === 'none') {
      return emp?.reportingManagerName || emp?.reportingManagerDetails?.name || null;
    }
    if (typeof raw === 'object') {
      return employeeDisplayName(raw) || raw?.name || null;
    }
    const key = String(raw);
    return (
      managerNameById.get(key) ||
      emp?.reportingManagerName ||
      emp?.reportingManagerDetails?.name ||
      null
    );
  };

  const companyOptions = useMemo(() => {
    return (companiesCatalog || [])
      .filter((company) => company?.companyId && company?.name)
      .map((company) => ({ id: company.companyId, name: company.name }));
  }, [companiesCatalog]);

  const getCompanyName = (emp) => {
    return emp?.companyDetails?.name ?? emp?.company ?? '-';
  };

  const filtered = employees;

  const getProjectNames = (projectIds) => {
    if (!projectIds || projectIds.length === 0) return [];
    return projectIds.map(pid => {
      const proj = projects.find(p => p.projectId === pid);
      return proj ? proj.name : pid;
    });
  };

  const getActiveExportFilters = () => {
    const params = {};
    const trimmedSearch = search?.trim?.();
    if (trimmedSearch) params.search = trimmedSearch;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (deptFilter !== 'all') params.department = deptFilter;
    if (designationFilter !== 'all') params.designation = designationFilter;
    if (projectFilter !== 'all') params.project = projectFilter;
    if (companyFilter !== 'all') params.company = companyFilter;
    return params;
  };

  const handleClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setDeptFilter('all');
    setDesignationFilter('all');
    setStatusFilter('Active');
    setCompanyFilter('all');
    setProjectFilter('all');
    setPage(1);
  };

  const handleExportEmployees = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const filters = getActiveExportFilters();
      const query = new URLSearchParams(filters).toString();
      const apiBase =
        process.env.NEXT_PUBLIC_API_URL?.replace(/\/+$/, '') ||
        process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') ||
        '';
      const token = localStorage.getItem('nexhr_token') || '';
      const response = await fetch(`${apiBase}/api/employees/export${query ? `?${query}` : ''}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error('Failed to export employees');

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName = matchedFileName || `employees_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlobFile(blob, fileName);

      toast.success('Employees exported');
    } catch (err) {
      console.error('Employee export failed:', err);
      toast.error(err?.message || 'Failed to export employees');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="employee-list-page" className="flex flex-col h-[calc(100vh-2rem)] md:h-[calc(100vh-3rem)] overflow-hidden animate-fadeIn">
      <div className="shrink-0 space-y-3 bg-background z-30">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Employees</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">{totalEmployees || employees.length} total employees</p>
          </div>
          {hasAccess('HR') && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={handleExportEmployees}
                disabled={exporting}
                data-testid="export-employees-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button data-testid="add-employee-btn" onClick={() => navigate('/employees/new')} className="bg-[#0047AB] hover:bg-[#003d96] text-white">
                <Plus className="w-4 h-4 mr-2" />Add Employee
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="border border-border shadow-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  data-testid="employee-search" 
                  placeholder="Search by name, ID, or phone..." 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  className="pl-10" 
                />
              </div>
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger data-testid="dept-filter" className="w-[160px] sm:w-[180px]">
                  <Filter className="w-3 h-3 mr-2" /><SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Depts</SelectItem>
                  {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={designationFilter} onValueChange={setDesignationFilter}>
                <SelectTrigger data-testid="designation-filter" className="w-[160px] sm:w-[180px]">
                  <SelectValue placeholder="Designation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Designations</SelectItem>
                  {DESIGNATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger data-testid="company-filter" className="w-[160px]">
                  <SelectValue placeholder="Company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companyOptions.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger data-testid="project-filter" className="w-[160px]">
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => <SelectItem key={p.projectId} value={p.projectId}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-[120px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={handleClearFilters} data-testid="employees-clear-filters-btn">
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Table */}
      <Card className="flex-1 min-h-0 border border-border shadow-sm overflow-hidden flex flex-col mt-3">
        <CardContent className="p-0 flex-1 min-h-0">
          <Table containerClassName="h-full overflow-auto">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Emp ID</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Phone</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Department</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Projects</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Leave Balance</TableHead>
                {/* <TableHead className="text-xs uppercase tracking-widest font-medium text-center sticky top-0 z-20 bg-muted">Confirmed</TableHead> */}
                <TableHead className="text-xs uppercase tracking-widest font-medium sticky top-0 z-20 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(emp => {
                const empId = emp.empId || emp.id;
                const projectNames = getProjectNames(emp.projects);
                const balance = emp?.leaveBalance;
                const reportingManagerName = getReportingManagerName(emp);
                const isReportingManager = Boolean(emp?.isReportingManager);

                return (
                  <TableRow 
                    key={empId} 
                    data-testid={`employee-row-${empId}`} 
                    className="cursor-pointer hover:bg-accent/50 transition-colors" 
                    onClick={() => navigate(`/employees/${empId}`)}
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">{empId}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {emp.picture ? (
                          <img src={emp.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#0047AB]/10 flex items-center justify-center text-xs font-bold text-[#0047AB]">
                            {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium truncate">{emp.firstName} {emp.lastName}</p>
                            <HoverCard openDelay={150} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-[#0047AB]/10 hover:text-[#0047AB] outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                  aria-label={
                                    reportingManagerName
                                      ? `Reporting manager: ${reportingManagerName}`
                                      : 'No reporting manager'
                                  }
                                  data-testid={`reporting-manager-${empId}`}
                                >
                                  <User className="h-3.5 w-3.5" />
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent align="start" className="w-56 p-3 space-y-1">
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Reporting Manager</p>
                                <p className="text-sm font-medium">{reportingManagerName || 'None'}</p>
                              </HoverCardContent>
                            </HoverCard>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-xs text-muted-foreground">{emp.designation}</p>
                            {isReportingManager && (
                              <Badge
                                className="text-[10px] px-1.5 py-0 h-5 bg-[#0047AB]/10 text-[#0047AB] border-[#0047AB]/20 shrink-0"
                                data-testid={`reporting-manager-flag-${empId}`}
                              >
                                Reporting Manager
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <p className="font-mono text-sm">{emp?.phone ?? '-'}</p>
                        <p className="text-xs  break-all">{emp?.email ?? '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{emp.department}</TableCell>
                    <TableCell className="text-sm">{getCompanyName(emp)}</TableCell>
                    <TableCell>
                      {projectNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {projectNames.slice(0, 2).map((name, i) => (
                            <Badge key={i} variant="outline" className="text-[10px]">{name}</Badge>
                          ))}
                          {projectNames.length > 2 && (
                            <Badge variant="outline" className="text-[10px]">+{projectNames.length - 2}</Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {balance ? (
                        <div className="text-xs">
                          <span className="text-[#0047AB] font-mono">EL:{balance.EL || 0}</span>
                          <span className="text-muted-foreground mx-1">|</span>
                          <span className="text-[#6366f1] font-mono">CL:{balance.CL || 0}</span>
                          <span className="text-muted-foreground mx-1">|</span>
                          <span className="text-green-600 font-mono">ML:{balance.ML || 0}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* <TableCell className="text-center">
                      {emp.isConfirmed ? (
                        <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="w-4 h-4 text-yellow-500 mx-auto" />
                      )}
                    </TableCell> */}
                    <TableCell>
                      <Badge
                        className={
                          emp.status === 'Active'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : emp.status === 'Draft'
                              ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                              : 'bg-red-100 text-red-700 border-red-200'
                        }
                      >
                        {emp.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No employees found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-3 pb-1">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1}
            data-testid="employees-prev-page-btn"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page >= totalPages}
            data-testid="employees-next-page-btn"
          >
            Next
          </Button>
        </div>
      </div>

    </div>
  );
}
